import React, { useState, useEffect } from "react";
import { X, DollarSign, FileText, CreditCard, CheckCircle2, AlertCircle, Clock, Loader2, ExternalLink } from "lucide-react";
import { adminService } from "../services/adminService";

// ── Helpers ───────────────────────────────────────────────────────────────────
const AVATAR_COLORS = [
  "#F97316", "#3B82F6", "#10B981", "#8B5CF6",
  "#EF4444", "#F59E0B", "#6366F1", "#14B8A6",
];

const getInitials = (name = "") =>
  name.split(" ").map((w) => w[0]).join("").toUpperCase().slice(0, 2) || "?";

const avatarColor = (name = "") => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
};

const planBadgeClass = (tier = "") => {
  switch (tier.toLowerCase()) {
    case "enterprise":   return "bg-purple-50 text-purple-600 border border-purple-200";
    case "professional": return "bg-blue-50   text-blue-600   border border-blue-200";
    case "business":      return "bg-orange-50 text-orange-600 border border-orange-200";
    case "individual":   return "bg-green-50  text-green-600  border border-green-200";
    default:             return "bg-gray-100  text-gray-600   border border-gray-200";
  }
};

const formatDate = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatAmount = (amount, currency = "USD") => {
  if (amount == null) return "—";
  const sym = currency.toUpperCase() === "INR" ? "₹" : "$";
  return `${sym}${Number(amount).toFixed(2)}`;
};

const StatusBadge = ({ status }) => {
  const s = (status || "").toLowerCase();
  if (s === "succeeded") return (
    <span className="inline-flex items-center gap-1.5 bg-emerald-50 text-emerald-600 text-xs font-semibold px-3 py-1 rounded-full border border-emerald-100">
      <CheckCircle2 className="w-3.5 h-3.5" />Succeeded
    </span>
  );
  if (s === "incomplete") return (
    <span className="inline-flex items-center gap-1.5 bg-amber-50 text-amber-600 text-xs font-semibold px-3 py-1 rounded-full border border-amber-100">
      <Clock className="w-3.5 h-3.5" />Incomplete
    </span>
  );
  return (
    <span className="inline-flex items-center gap-1.5 bg-red-50 text-red-500 text-xs font-semibold px-3 py-1 rounded-full border border-red-100">
      <AlertCircle className="w-3.5 h-3.5" />{status ?? "—"}
    </span>
  );
};

// ── Sub-components ────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, iconBg, iconColor, value, label }) => (
  <div className="flex items-center gap-4 border border-surface-border rounded-xl px-5 py-4 bg-white flex-1">
    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg}`}>
      <Icon className={`w-5 h-5 ${iconColor}`} />
    </div>
    <div>
      <p className="text-lg font-bold text-brand-text leading-tight">{value}</p>
      <p className="text-xs text-brand-muted">{label}</p>
    </div>
  </div>
);

// ── Modal ─────────────────────────────────────────────────────────────────────
const PaymentHistoryModal = ({ user, onClose }) => {
  const [payments,  setPayments]  = useState([]);
  const [summary,   setSummary]   = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  useEffect(() => {
    if (!user?.id) return;
    let cancelled = false;
    setLoading(true);
    adminService.getPaymentHistory(user.id, { limit: 20 })
      .then((res) => {
        if (cancelled) return;
        const d = res?.data ?? res;
        setPayments(Array.isArray(d?.payments) ? d.payments : []);
        setSummary(d?.summary ?? null);
        setError(null);
      })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [user?.id]);

  const name  = user?.name  || "User";
  const email = user?.email || "";
  const org   = user?.organization ?? user?.company ?? "";
  const color = avatarColor(name);

  const totalRevenue      = summary?.total_revenue      ?? 0;
  const totalTransactions = summary?.total_transactions ?? payments.length;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="px-6 py-5 border-b border-gray-100 flex items-center gap-4">
          <div
            className="w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-white text-sm font-bold select-none"
            style={{ background: color }}
          >
            {getInitials(name)}
          </div>
          <div className="flex-1 min-w-0">
            <h2 className="text-base font-bold text-brand-text">Payment History</h2>
            <p className="text-xs text-brand-muted truncate mt-0.5">
              {[name, email, org].filter(Boolean).join(" · ")}
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full text-brand-muted hover:bg-gray-100 transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Stats */}
        <div className="px-6 py-4 flex gap-4 border-b border-gray-100">
          <StatCard
            icon={DollarSign}
            iconBg="bg-emerald-50"
            iconColor="text-emerald-500"
            value={loading ? "…" : `$${Number(totalRevenue).toFixed(2)}`}
            label="Total Revenue"
          />
          <StatCard
            icon={FileText}
            iconBg="bg-blue-50"
            iconColor="text-blue-500"
            value={loading ? "…" : totalTransactions}
            label="Transactions"
          />
          <div className="flex-1 hidden sm:block" />
          <div className="flex-1 hidden sm:block" />
        </div>

        {/* Body */}
        <div className="overflow-y-auto flex-1 px-4 py-3">

          {loading && (
            <div className="flex items-center justify-center py-16 gap-2 text-brand-muted">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">Loading payments…</span>
            </div>
          )}

          {!loading && error && (
            <p className="text-center text-sm text-red-500 py-10">{error}</p>
          )}

          {!loading && !error && payments.length === 0 && (
            <p className="text-center text-sm text-brand-muted py-10">No payment records found.</p>
          )}

          {!loading && !error && payments.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-brand-muted whitespace-nowrap">S.No</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-brand-muted whitespace-nowrap">Invoice ID</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-brand-muted whitespace-nowrap">Date</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-brand-muted whitespace-nowrap">Amount</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-brand-muted whitespace-nowrap">Payment Method</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-brand-muted whitespace-nowrap">Plan</th>
                  <th className="px-4 py-3.5 text-left text-xs font-semibold text-brand-muted whitespace-nowrap">Status</th>
                  <th className="px-4 py-3.5 text-center text-xs font-semibold text-brand-muted whitespace-nowrap">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {payments.map((tx, idx) => (
                  <tr key={tx.invoice_id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50 transition-colors">

                    {/* S.No */}
                    <td className="px-4 py-3.5 text-center text-sm text-brand-muted">{idx + 1}</td>

                    {/* Payment ID */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className="bg-blue-50 text-blue-600 text-xs font-semibold px-2.5 py-1 rounded-full border border-blue-100">
                        {tx.invoice_id ?? "—"}
                      </span>
                    </td>

                    {/* Date */}
                    <td className="px-4 py-3.5 text-brand-muted whitespace-nowrap">{formatDate(tx.date)}</td>

                    {/* Amount */}
                    <td className="px-4 py-3.5 font-semibold text-brand-text whitespace-nowrap text-right">
                      {formatAmount(tx.amount, tx.currency)}
                    </td>

                    {/* Payment Method */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <div className="flex items-center gap-1.5 text-brand-text">
                        <CreditCard className="w-3.5 h-3.5 text-brand-muted flex-shrink-0" />
                        <span className="text-sm">{tx.payment_method?.display ?? <span className="text-brand-muted">—</span>}</span>
                      </div>
                    </td>

                    {/* Plan */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full whitespace-nowrap ${planBadgeClass(tx.plan ?? "")}`}>
                        {tx.plan ?? "—"}
                      </span>
                    </td>

                    {/* Status */}
                    <td className="px-4 py-3.5 whitespace-nowrap">
                      <StatusBadge status={tx.status} />
                    </td>

                    {/* Receipt */}
                    <td className="px-4 py-3.5 text-center">
                      {tx.receipt_url ? (
                        <a
                          href={tx.receipt_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center justify-center text-blue-600 hover:text-blue-700 transition-colors"
                          title="Open receipt"
                        >
                          <ExternalLink className="w-4 h-4" />
                        </a>
                      ) : (
                        <span className="inline-flex items-center justify-center text-brand-muted opacity-40">
                          <FileText className="w-4 h-4" />
                        </span>
                      )}
                    </td>

                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentHistoryModal;

